/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ArrowLeft, ExternalLink, Pencil, Play, Send } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '../../api';
import type { IntegrationEnvironment } from '../../api/areas/integrations';
import {
  CORE_MODULE,
  type ModuleActionResult,
  type ModuleBranchId,
  type ModuleCall,
  type ModuleField,
  type ModuleInstance,
  type ModuleInstanceDetail,
  type ModuleManifest,
  type ModuleTest,
  type ModuleVersion,
  type ModuleWebhookReceipt,
} from '../../api/areas/modules';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Field, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { useRetained, useWrite } from '../../components/Write';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { modulePage } from '../../modules/pages';
import { useCatalogue } from '../admin/ui';
import { MODULE_CHANGE_KINDS, PendingChangesPanel } from '../integrations/changes';
import { ChangeDialog, EnvironmentBanner, OnOffBadge, Panel, ReasonField, SelectField, ServiceName, StaffName, When } from '../integrations/shared';
import { checkValue, formatBytes, initialValue, isBlank, isRequired, payloadValue, type FormValue } from './fieldKinds';
import { ProposeForm, ToggleForm } from './forms';
import { HostingInstancePanel } from './HostingInstancePanel';
import { FieldInput, SchemaForm } from './SchemaForm';
import {
  DocList,
  HealthBadge,
  InstanceStatusBadge,
  ModuleError,
  modulesUrl,
  StepLine,
  useCapabilityName,
  useCodeMeaning,
  useInstanceLabel,
  useOperationName,
  useText,
  VersionStatusBadge,
  VersionValues,
} from './ui';

const PANES = ['overview', 'config', 'test', 'callback', 'logs', 'actions', 'pages'] as const;
type Pane = (typeof PANES)[number];

interface PaneProps {
  data: ModuleInstanceDetail;
  fields: readonly ModuleField[];
  branch: ModuleBranchId;
  tick: number;
  onChanged: () => void;
  openPane: (p: Pane) => void;
}

/**
 * One connection (instance) of a module (ADR-025 §8): Tổng quan · Cấu hình · Kiểm tra kết nối ·
 * Callback · Nhật ký · Thao tác · Trang nghiệp vụ.
 */
export function InstancePage({ branch, module, instanceKey }: { branch: ModuleBranchId; module: string; instanceKey: string }) {
  const { t } = useTranslation('modules');
  const text = useText();
  const label = useInstanceLabel();
  const [params, setParams] = useSearchParams();
  const [tick, setTick] = useState(0);
  const result = useApi((signal) => api.getModuleInstance(branch, module, instanceKey, { signal }), [branch, module, instanceKey, tick]);
  const data = useRetained(result.data);
  const onChanged = () => setTick((n) => n + 1);
  const raw = params.get('tab');
  const pane: Pane = PANES.find((p) => p === raw) ?? 'overview';
  const openPane = (p: Pane) => {
    const next = new URLSearchParams(params);
    next.set('tab', p);
    setParams(next, { replace: true });
  };
  const back = (
    <Button variant="ghost" size="sm" asChild className="-ml-2 w-fit">
      <Link to={modulesUrl(branch)}>
        <ArrowLeft /> {t('page.backTo', { branch: t(`branch.${branch}`) })}
      </Link>
    </Button>
  );
  if (result.status === 'error' && !data) {
    return (
      <div className="space-y-4">
        {back}
        <DataError error={result.error} onRetry={result.reload} title={t('instance.loadFailed')} />
      </div>
    );
  }
  if (!data) return <Loading rows={5} />;
  const { instance, manifest } = data;
  const fields = [...data.common_settings, ...manifest.settings.fields];
  const props: PaneProps = { data, fields, branch, tick, onChanged, openPane };
  // The branch core's own settings: one page, the settings and their versions.
  const isCore = manifest.id === CORE_MODULE;
  return (
    <div className="space-y-5" data-module-instance={`${branch}/${module}/${instanceKey}`}>
      {back}
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">
          {isCore ? t('instance.coreTitle', { branch: t(`branch.${branch}`) }) : t('instance.title', { module: text(manifest.name), instance: label(instance) })}
        </h2>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <InstanceStatusBadge status={instance.status} />
            {isCore ? null : <HealthBadge health={instance.health} />}
            <code className="text-xs text-muted-foreground">
              {manifest.branch}/{manifest.id}/{instance.key}
            </code>
          </p>
          {isCore ? null : <InstanceToggle data={data} env={data.environment} onChanged={onChanged} />}
        </div>
      </div>
      <EnvironmentBanner env={data.environment} />
      {isCore ? (
        <ConfigPane {...props} />
      ) : (
        <Tabs value={pane} onValueChange={(v) => openPane(PANES.find((p) => p === v) ?? 'overview')}>
          <TabsList className="flex h-auto w-full flex-wrap justify-start">
            {PANES.map((p) => (
              <TabsTrigger key={p} value={p} className="px-3">
                {t(`instance.tabs.${p}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview" className="mt-4 min-w-0">
            <OverviewPane {...props} />
          </TabsContent>
          <TabsContent value="config" className="mt-4 min-w-0">
            <ConfigPane {...props} />
          </TabsContent>
          <TabsContent value="test" className="mt-4 min-w-0">
            <TestPane {...props} />
          </TabsContent>
          <TabsContent value="callback" className="mt-4 min-w-0">
            <CallbackPane {...props} />
          </TabsContent>
          <TabsContent value="logs" className="mt-4 min-w-0">
            <LogsPane {...props} />
          </TabsContent>
          <TabsContent value="actions" className="mt-4 min-w-0">
            <ActionsPane {...props} />
          </TabsContent>
          <TabsContent value="pages" className="mt-4 min-w-0">
            <PagesPane {...props} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tổng quan

function scheduleText(t: (k: string, o?: Record<string, unknown>) => string, s: ModuleManifest['jobs'][number]['schedule'], fieldName: (k: string) => string): string {
  if (s.kind === 'daily_at') return t('instance.overview.dailyAt', { setting: fieldName(s.setting), time: s.default });
  if (s.seconds % 3600 === 0) return t('instance.overview.everyHours', { count: s.seconds / 3600 });
  if (s.seconds % 60 === 0) return t('instance.overview.everyMinutes', { count: s.seconds / 60 });
  return t('instance.overview.everySeconds', { count: s.seconds });
}

function OverviewPane({ data, fields }: PaneProps) {
  const { t } = useTranslation('modules');
  const text = useText();
  const capability = useCapabilityName();
  const { instance: i, manifest: m } = data;
  const loose = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const fieldName = (k: string) => text(fields.find((f) => f.key === k)?.label) || k;
  return (
    <div className="space-y-6">
      <Panel title={t('instance.overview.state')}>
        <KeyValue
          columns={2}
          rows={[
            [
              t('instance.overview.state'),
              <span key="s" className="grid gap-1">
                <InstanceStatusBadge status={i.status} />
                <span className="text-xs text-muted-foreground">{t(`statusHint.${i.status}`)}</span>
              </span>,
            ],
            [
              t('instanceColumns.health'),
              <span key="h" className="grid gap-1">
                <HealthBadge health={i.health} />
                <span className="text-xs text-muted-foreground">{t(`healthHint.${i.health}`)}</span>
              </span>,
            ],
            [t('instance.overview.enabled'), <OnOffBadge key="e" on={i.enabled} />],
            [
              t('instance.overview.inForce'),
              i.active ? (
                <span key="v" className="grid gap-0.5">
                  <span>{t('card.versionInForce', { version: i.active.version })}</span>
                  <span className="inline-flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                    <StaffName staff={i.active.activated_by} /> · <When at={i.active.activated_at} />
                  </span>
                </span>
              ) : (
                t('instance.overview.noneInForce')
              ),
            ],
            [t('instance.overview.displayName'), i.display_name ?? '—'],
            [t('instance.overview.key'), <code key="k">{i.key}</code>],
            i.generation !== null ? [t('instance.overview.generation'), formatCount(i.generation)] : null,
          ]}
        />
      </Panel>
      <Panel title={t('instance.overview.applied')} description={t('instance.overview.appliedHint')}>
        {i.applied_by.length ? (
          <ul className="grid gap-2">
            {i.applied_by.map((a) => (
              <li key={`${a.service}/${a.instance}/${a.subject ?? ''}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-2 text-sm">
                <ServiceName service={a.service} instance={a.instance} />
                <span className="flex flex-wrap items-center gap-2 text-xs">
                  {a.former_subject ? (
                    <span title={t('instance.overview.formerSubjectHint', { subject: a.subject ?? '' })}>
                      <Badge tone="neutral">{t('instance.overview.formerSubject')}</Badge>
                    </span>
                  ) : null}
                  <span className="text-muted-foreground">{t(`instance.source.${a.source}`)}</span>
                  {a.last_error ? (
                    <Badge tone="crit">{loose(`instance.loadError.${a.last_error.code}`, { defaultValue: t('instance.loadError.other') })}</Badge>
                  ) : (
                    <Badge tone={a.up_to_date ? 'ok' : 'warn'}>{a.up_to_date ? t('instance.overview.upToDate') : t('instance.overview.behind')}</Badge>
                  )}
                  <When at={a.checked_at} />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{t('instance.overview.appliedNone')}</p>
        )}
      </Panel>
      <Panel title={t('instance.overview.module')} description={text(m.summary)}>
        <KeyValue
          columns={2}
          rows={[
            [
              t('instance.overview.vendor'),
              <a key="v" href={m.vendor.website} target="_blank" rel="noreferrer noopener" className="text-primary hover:underline">
                {m.vendor.name}
              </a>,
            ],
            [t('instance.overview.moduleVersion'), m.version],
            [t('instance.overview.data'), t(`data.${m.data}`)],
            [t('instance.overview.egress'), m.egress.kind === 'hosts'
                ? t('egress.hosts', { hosts: m.egress.hosts.join(', ') })
                : m.egress.kind === 'from_setting'
                  ? t('egress.from_setting', { setting: m.egress.setting })
                  : t(`egress.${m.egress.kind}`)],
            [t('instance.overview.disable'), t(`disable.${m.disable}`)],
            [t('instance.overview.instances'), m.instances.kind === 'one' ? t('instancesPolicy.one') : t('instancesPolicy.many', { max: m.instances.max })],
            [
              t('instance.overview.testPolicy'),
              m.test.kind === 'steps'
                ? t('testPolicy.steps', { steps: m.test.steps.map((s) => t(`test.step.${s}`)).join(', ') })
                : t('testPolicy.no_test', { reason: text(m.test.reason) }),
            ],
            [
              t('card.capabilities'),
              m.capabilities.length ? (
                <span key="c" className="flex flex-wrap gap-1">
                  {m.capabilities.map((c) => (
                    <Badge key={c} tone="info" icon={false} title={c}>
                      {capability(c)}
                    </Badge>
                  ))}
                </span>
              ) : (
                t('card.noCapabilities')
              ),
            ],
            [
              t('instance.overview.jobs'),
              m.jobs.length ? (
                <ul key="j" className="grid gap-0.5">
                  {m.jobs.map((j) => (
                    <li key={j.key}>
                      {text(j.label)} <span className="text-xs text-muted-foreground">({scheduleText(loose, j.schedule, fieldName)})</span>
                    </li>
                  ))}
                </ul>
              ) : (
                t('instance.overview.noJobs')
              ),
            ],
            [t('instance.overview.events'), m.events.length ? m.events.map((e) => text(e.label)).join(', ') : t('instance.overview.noJobs')],
            m.docs.length ? [t('card.documents'), <DocList key="d" docs={m.docs} />] : null,
          ]}
        />
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cấu hình

function VersionHistory({ data, fields, tick }: PaneProps) {
  const { t } = useTranslation('modules');
  const { manifest: m, instance: i } = data;
  const [before, setBefore] = useState<number[]>([]);
  const cursor = before[before.length - 1];
  const result = useApi((signal) => api.listModuleVersions(m.branch, m.id, i.key, { before_version: cursor, limit: 10 }, { signal }), [m.branch, m.id, i.key, cursor, tick]);
  const page = useRetained(result.data);
  const columns: Column<ModuleVersion>[] = [
    { key: 'v', header: t('config.columns.version'), cell: (v) => v.version, numeric: true },
    {
      key: 's',
      header: t('config.columns.status'),
      cell: (v) => (
        <span className="grid gap-1">
          <VersionStatusBadge status={v.status} />
        </span>
      ),
    },
    { key: 'e', header: t('config.columns.enabled'), cell: (v) => <OnOffBadge on={v.enabled} /> },
    {
      key: 'values',
      header: t('config.columns.values'),
      cell: (v) => (
        <details className="text-xs">
          <summary className="cursor-pointer text-primary">{t('config.columns.values')}</summary>
          <div className="mt-2">
            <VersionValues version={v} fields={fields} compact />
          </div>
        </details>
      ),
    },
    { key: 'r', header: t('config.columns.reason'), cell: (v) => v.reason, className: 'min-w-40' },
    {
      key: 'by',
      header: t('config.columns.by'),
      cell: (v) => (
        <span className="grid gap-0.5 text-xs">
          <StaffName staff={v.created_by} />
          <When at={v.created_at} />
        </span>
      ),
    },
  ];
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} />;
  if (!page) return <Loading rows={2} />;
  return (
    <>
      <DataTable columns={columns} rows={page.items} rowKey={(v) => v.id} empty={<p className="px-5 py-4 text-sm text-muted-foreground">{t('config.historyNone')}</p>} />
      {page.next_before_version !== null || before.length ? (
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <Button size="sm" variant="outline" disabled={!before.length} onClick={() => setBefore((b) => b.slice(0, -1))}>
            {t('config.newer')}
          </Button>
          <Button size="sm" variant="outline" disabled={page.next_before_version === null} onClick={() => page.next_before_version !== null && setBefore((b) => [...b, page.next_before_version as number])}>
            {t('config.older')}
          </Button>
        </div>
      ) : null}
    </>
  );
}

function TestedLine({ version, noTest }: { version: ModuleVersion; noTest: boolean }) {
  const { t } = useTranslation('modules');
  if (noTest) return <p className="text-xs text-muted-foreground">{t('config.noTestNeeded')}</p>;
  return version.tested ? <Badge tone="ok">{t('config.tested')}</Badge> : <Badge tone="warn">{t('config.notTested')}</Badge>;
}

function ConfigPane(props: PaneProps) {
  const { data, fields, branch, tick, onChanged } = props;
  const { t } = useTranslation('modules');
  const text = useText();
  const { instance: i, manifest: m } = data;
  const [dialog, setDialog] = useState<'edit' | 'propose' | null>(null);
  const noTest = m.test.kind === 'no_test';
  const production = data.environment === 'PRODUCTION';
  const base = i.draft ?? i.active;
  return (
    <div className="space-y-6">
      <PendingChangesPanel kinds={MODULE_CHANGE_KINDS} module={{ branch, module: m.id, instance_key: i.key }} refreshKey={tick} onChanged={onChanged} />
      <Panel
        title={t('config.inForce')}
        actions={
          data.can_manage ? (
            <Button size="sm" onClick={() => setDialog('edit')} disabled={!!i.pending}>
              <Pencil /> {base ? t('config.edit') : t('config.create')}
            </Button>
          ) : null
        }
      >
        {i.active ? (
          <div className="space-y-3">
            <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {t('card.versionInForce', { version: i.active.version })} · <StaffName staff={i.active.activated_by} /> · <When at={i.active.activated_at} />
            </p>
            <VersionValues version={i.active} fields={fields} />
            <p className="text-sm">
              <span className="text-muted-foreground">{t('config.reason')}:</span> {i.active.reason}
            </p>
          </div>
        ) : (
          <EmptyState title={t('config.nothing')} />
        )}
      </Panel>
      {i.draft ? (
        <Panel
          title={`${t('config.draft')} · ${t('test.forVersion', { version: i.draft.version })}`}
          className="border-primary/40"
          description={m.id === CORE_MODULE ? undefined : <TestedLine version={i.draft} noTest={noTest} />}
          actions={
            data.can_manage ? (
              <>
                {!noTest ? <RunTest data={data} version={i.draft} onTested={onChanged} label={t('config.test')} /> : null}
                <Button size="sm" onClick={() => setDialog('propose')} disabled={!i.draft.tested || !!i.pending} title={!i.draft.tested ? t('config.proposeNeedsTest') : undefined}>
                  <Send /> {production ? t('config.proposeProduction') : t('config.propose')}
                </Button>
              </>
            ) : null
          }
        >
          <div className="space-y-3">
            {!i.draft.tested && !noTest ? <Callout tone="info">{t('config.proposeNeedsTest')}</Callout> : null}
            <VersionValues version={i.draft} fields={fields} />
            <p className="text-xs text-muted-foreground">
              {t('config.savedBy', { who: i.draft.created_by?.display_name ?? '—' })} · <When at={i.draft.created_at} /> · {i.draft.reason}
            </p>
          </div>
        </Panel>
      ) : null}
      {i.pending ? (
        <Panel title={`${t('config.pending')} · ${t('test.forVersion', { version: i.pending.version })}`} className="border-warning/40">
          <VersionValues version={i.pending} fields={fields} />
        </Panel>
      ) : null}
      <Panel title={t('config.history')} flush>
        <VersionHistory {...props} />
      </Panel>
      <ChangeDialog open={dialog === 'edit'} onOpenChange={(o) => !o && setDialog(null)} title={t('form.title', { name: text(m.name) })} description={t('form.description')} wide>
        <SchemaForm manifest={m} fields={fields} instance={i} base={base} env={data.environment} canManageSecrets={data.can_manage_secrets} onClose={() => setDialog(null)} onDone={onChanged} />
      </ChangeDialog>
      <ChangeDialog open={dialog === 'propose' && !!i.draft} onOpenChange={(o) => !o && setDialog(null)} title={t('propose.title', { version: i.draft?.version ?? '' })}>
        {i.draft ? <ProposeForm manifest={m} instance={i} draft={i.draft} fields={fields} env={data.environment} onClose={() => setDialog(null)} onDone={onChanged} /> : null}
      </ChangeDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kiểm tra kết nối

function TestResult({ test, versions }: { test: ModuleTest; versions: readonly (ModuleVersion | null)[] }) {
  const { t } = useTranslation('modules');
  const v = versions.find((x) => x && x.id === test.version_id);
  return (
    <div className="space-y-2 rounded-lg border p-3">
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={test.outcome === 'OK' ? 'ok' : 'crit'}>{t(`test.outcome.${test.outcome}`)}</Badge>
        {v ? <span className="text-muted-foreground">{t('test.forVersion', { version: v.version })}</span> : null}
        <When at={test.finished_at} className="text-xs text-muted-foreground" />
      </p>
      <ul className="grid gap-2">
        {test.steps.map((s, idx) => (
          <StepLine key={`${s.step}-${idx}`} step={s} />
        ))}
      </ul>
    </div>
  );
}

/** "Kiểm tra kết nối" of one version, with its result shown inline. */
function RunTest({ data, version, onTested, label }: { data: ModuleInstanceDetail; version: ModuleVersion; onTested: () => void; label?: string }) {
  const { t } = useTranslation('modules');
  const { manifest: m, instance: i } = data;
  const write = useWrite((body: { version_id: string }, opts) => api.testModuleInstance(m.branch, m.id, i.key, body, opts));
  const [open, setOpen] = useState(false);
  const run = async () => {
    setOpen(true);
    const r = await write.run({ version_id: version.id });
    if (r) onTested();
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => void run()} disabled={write.busy}>
        <Play /> {write.busy ? t('test.running') : (label ?? t('test.run'))}
      </Button>
      <ChangeDialog open={open} onOpenChange={setOpen} title={t('test.title')} description={t('test.forVersion', { version: version.version })}>
        {write.busy ? <Loading label={t('test.running')} /> : null}
        {write.state.phase === 'done' ? <TestResult test={write.state.result.test} versions={[version]} /> : null}
        {write.state.phase === 'error' ? <ModuleError error={write.state.error} /> : null}
      </ChangeDialog>
    </>
  );
}

function TestPane({ data, onChanged }: PaneProps) {
  const { t } = useTranslation('modules');
  const text = useText();
  const { instance: i, manifest: m } = data;
  const choices = [
    i.draft ? { value: i.draft.id, label: t('test.versionOf.draft', { version: i.draft.version }), version: i.draft } : null,
    i.pending ? { value: i.pending.id, label: t('test.versionOf.pending', { version: i.pending.version }), version: i.pending } : null,
    i.active ? { value: i.active.id, label: t('test.versionOf.active', { version: i.active.version }), version: i.active } : null,
  ].filter((x): x is { value: string; label: string; version: ModuleVersion } => x !== null);
  const [picked, setPicked] = useState<string>(choices[0]?.value ?? '');
  const chosen = choices.find((c) => c.value === picked) ?? choices[0];
  if (m.test.kind === 'no_test') {
    return (
      <Callout tone="info" title={t('test.title')}>
        {t('testPolicy.no_test', { reason: text(m.test.reason) })}
      </Callout>
    );
  }
  return (
    <div className="space-y-6">
      <Panel title={t('test.title')} description={t('test.hint')} actions={data.can_manage && chosen ? <RunTest data={data} version={chosen.version} onTested={onChanged} /> : null}>
        {choices.length > 1 ? <SelectField label={t('test.version')} value={chosen?.value ?? ''} onChange={setPicked} options={choices.map((c) => ({ value: c.value, label: c.label }))} /> : null}
        {!choices.length ? <p className="text-sm text-muted-foreground">{t('test.nothingToTest')}</p> : null}
        {choices.length === 1 && chosen ? <p className="text-sm text-muted-foreground">{chosen.label}</p> : null}
      </Panel>
      <Panel title={t('test.history')}>
        {i.recent_tests.length ? (
          <div className="grid gap-3">
            {i.recent_tests.map((x) => (
              <TestResult key={x.id} test={x} versions={[i.draft, i.pending, i.active]} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('test.none')}</p>
        )}
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Callback

const RECEIPT_TONE: Record<ModuleWebhookReceipt['outcome'], Tone> = { PROCESSED: 'ok', DUPLICATE: 'neutral', MALFORMED: 'warn', FAILED: 'crit' };

function useLogPage<T>(load: (before: string | undefined, signal: AbortSignal) => Promise<{ items: T[]; next_before: string | null }>, deps: unknown[]) {
  const [before, setBefore] = useState<string[]>([]);
  const cursor = before[before.length - 1];
  // oxlint-disable-next-line react/exhaustive-deps -- the caller's deps name what `load` reads.
  const result = useApi((signal) => load(cursor, signal), [cursor, ...deps]);
  const page = useRetained(result.data);
  const pager =
    page && (page.next_before !== null || before.length) ? (
      <PagerButtons newer={before.length ? () => setBefore((b) => b.slice(0, -1)) : null} older={page.next_before !== null ? () => setBefore((b) => [...b, page.next_before as string]) : null} />
    ) : null;
  return { result, page, pager };
}

function PagerButtons({ newer, older }: { newer: (() => void) | null; older: (() => void) | null }) {
  const { t } = useTranslation('modules');
  return (
    <div className="flex justify-end gap-2 border-t px-4 py-3">
      <Button size="sm" variant="outline" disabled={!newer} onClick={() => newer?.()}>
        {t('logs.newest')}
      </Button>
      <Button size="sm" variant="outline" disabled={!older} onClick={() => older?.()}>
        {t('logs.more')}
      </Button>
    </div>
  );
}

function Receipts({ data, tick }: PaneProps) {
  const { t } = useTranslation('modules');
  const { instance: i, manifest: m } = data;
  const { result, page, pager } = useLogPage<ModuleWebhookReceipt>((before, signal) => api.listModuleWebhooks(m.branch, m.id, i.key, { before, limit: 50 }, { signal }), [m.branch, m.id, i.key, tick]);
  const columns: Column<ModuleWebhookReceipt>[] = [
    { key: 'at', header: t('callback.columns.at'), cell: (r) => <When at={r.received_at} /> },
    { key: 'outcome', header: t('callback.columns.outcome'), cell: (r) => <Badge tone={RECEIPT_TONE[r.outcome]}>{t(`callback.receiptOutcome.${r.outcome}`)}</Badge> },
    { key: 'ids', header: t('callback.columns.ids'), cell: (r) => (r.external_ids.length ? <code className="text-xs">{r.external_ids.join(', ')}</code> : '—') },
    { key: 'size', header: t('callback.columns.size'), cell: (r) => formatBytes(r.body_size), numeric: true },
    { key: 'status', header: t('callback.columns.status'), cell: (r) => <code className="text-xs">{r.http_status}</code>, numeric: true },
    { key: 'duration', header: t('callback.columns.duration'), cell: (r) => t('test.duration', { ms: formatCount(r.duration_ms) }), numeric: true },
  ];
  return (
    <Panel title={t('callback.receipts')} description={t('callback.receiptsHint')} flush>
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
      {!page && result.status !== 'error' ? <Loading rows={2} /> : null}
      {page ? <DataTable columns={columns} rows={page.items} rowKey={(r) => r.id} empty={<p className="px-5 py-4 text-sm text-muted-foreground">{t('callback.receiptsNone')}</p>} /> : null}
      {pager}
    </Panel>
  );
}

function CallbackPane(props: PaneProps) {
  const { t } = useTranslation('modules');
  const { instance: i, manifest: m } = props.data;
  if (!m.webhook || !i.callback_path) return <EmptyState title={t('callback.none')} />;
  return (
    <div className="space-y-6">
      <Panel title={t('callback.title')}>
        <div className="space-y-3 text-sm">
          <Field label={t('callback.path')}>
            <CopyText value={i.callback_path} />
          </Field>
          <p className="text-muted-foreground">{t('callback.pathHint', { path: i.callback_path })}</p>
          <p>{m.webhook.get ? t('callback.methodGet') : t('callback.method')}</p>
          <p>{t('callback.limits', { size: formatBytes(m.webhook.max_body), seconds: m.webhook.answer_within_seconds })}</p>
          <p>{t('callback.security')}</p>
          {m.docs.length ? (
            <div className="space-y-1">
              <p className="font-medium">{t('callback.setup')}</p>
              <DocList docs={m.docs} />
            </div>
          ) : null}
        </div>
      </Panel>
      <Receipts {...props} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nhật ký

const CALL_TONE: Record<ModuleCall['outcome'], Tone> = { ok: 'ok', refused: 'warn', not_sent: 'crit', unknown: 'serious' };

function LogsPane({ data, branch, tick }: PaneProps) {
  const { t } = useTranslation('modules');
  const { instance: i, manifest: m } = data;
  const operation = useOperationName(m);
  const meaning = useCodeMeaning();
  const { result, page, pager } = useLogPage<ModuleCall>((before, signal) => api.listModuleCalls(m.branch, m.id, i.key, { before, limit: 50 }, { signal }), [m.branch, m.id, i.key, tick]);
  const columns: Column<ModuleCall>[] = [
    { key: 'at', header: t('logs.columns.at'), cell: (c) => <When at={c.started_at} /> },
    { key: 'op', header: t('logs.columns.operation'), cell: (c) => operation(c) },
    { key: 'outcome', header: t('logs.columns.outcome'), cell: (c) => <Badge tone={CALL_TONE[c.outcome]}>{t(`logs.callOutcome.${c.outcome}`)}</Badge> },
    {
      key: 'code',
      header: t('logs.columns.code'),
      cell: (c) =>
        c.code ? (
          <span className="grid gap-0.5 text-xs">
            <span>{meaning(c.code)}</span>
            <code className="text-muted-foreground">
              {c.code}
              {c.provider_code ? ` · ${c.provider_code}` : ''}
            </code>
          </span>
        ) : (
          '—'
        ),
    },
    { key: 'duration', header: t('logs.columns.duration'), cell: (c) => t('test.duration', { ms: formatCount(c.duration_ms) }), numeric: true },
    { key: 'version', header: t('logs.columns.version'), cell: (c) => <code className="text-xs">{c.module_version}</code> },
  ];
  const audit = `/settings?${new URLSearchParams({ tab: 'security', sub: 'audit', resource_type: 'module_instance', resource_id: `${branch}/${m.id}/${i.key}` }).toString()}`;
  return (
    <div className="space-y-6">
      <Panel
        title={t('logs.calls')}
        description={t('logs.callsHint')}
        actions={
          <Button size="sm" variant="outline" asChild>
            <Link to={audit}>
              <ExternalLink /> {t('logs.audit')}
            </Link>
          </Button>
        }
        flush
      >
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
        {!page && result.status !== 'error' ? <Loading rows={2} /> : null}
        {page ? <DataTable columns={columns} rows={page.items} rowKey={(c) => c.id} empty={<p className="px-5 py-4 text-sm text-muted-foreground">{t('logs.callsNone')}</p>} /> : null}
        {pager}
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thao tác

/** An action's result: a list as a table, an object as key / value, a refusal or failure in words. */
function ActionResultView({ result }: { result: ModuleActionResult }) {
  const { t } = useTranslation('modules');
  const meaning = useCodeMeaning();
  const tone: Tone = result.outcome === 'ok' ? 'ok' : result.outcome === 'refused' ? 'warn' : 'crit';
  const cell = (v: unknown): ReactNode => (v === null || v === undefined ? '—' : typeof v === 'object' ? <code className="text-xs">{JSON.stringify(v)}</code> : String(v));
  const value = result.value;
  let body: ReactNode = null;
  if (result.outcome === 'ok') {
    if (Array.isArray(value)) {
      const rows = value.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x));
      const keys = [...new Set(rows.flatMap((r) => Object.keys(r)))];
      body = rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {keys.map((k) => (
                  <th key={k} className="px-2 py-1 text-left text-xs font-medium text-muted-foreground">
                    {k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} className="border-t">
                  {keys.map((k) => (
                    <td key={k} className="px-2 py-1">
                      {cell(r[k])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : value.length ? (
        <p className="text-sm">{value.map((x) => String(x)).join(', ')}</p>
      ) : (
        <p className="text-sm text-muted-foreground">{t('actions.empty')}</p>
      );
    } else if (value && typeof value === 'object') {
      body = <KeyValue columns={2} rows={Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, cell(v)] as const)} />;
    } else {
      body = <p className="text-sm">{value === undefined || value === null ? t('actions.empty') : String(value)}</p>;
    }
  } else if (result.refusal) {
    body = (
      <p className="text-sm">
        {meaning(result.refusal.code)} <code className="text-xs text-muted-foreground">{result.refusal.code}</code>
        {result.refusal.message ? <span className="block break-words font-mono text-xs text-muted-foreground">{result.refusal.message}</span> : null}
      </p>
    );
  } else if (result.failure) {
    body = (
      <p className="text-sm">
        {meaning(result.failure.class)} <code className="text-xs text-muted-foreground">{result.failure.class}</code>
        <span className="block break-words font-mono text-xs text-muted-foreground">{result.failure.detail}</span>
      </p>
    );
  }
  return (
    <div className="space-y-2 rounded-lg border p-3">
      <p className="flex items-center gap-2 text-sm font-medium">
        {t('actions.result')} <Badge tone={tone}>{t(`actions.outcome.${result.outcome}`)}</Badge>
      </p>
      {body}
    </div>
  );
}

function ActionCard({ data, spec, fields }: { data: ModuleInstanceDetail; spec: ModuleManifest['admin_actions'][number]; fields: readonly ModuleField[] }) {
  const { t } = useTranslation('modules');
  const text = useText();
  const { permissionName } = useCatalogue();
  const { instance: i, manifest: m } = data;
  const inputs = spec.input?.fields ?? [];
  const [values, setValues] = useState<Record<string, FormValue>>(() => Object.fromEntries(inputs.map((f) => [f.key, initialValue(f, null)])));
  const [reason, setReason] = useState('');
  const write = useWrite((body: { input: Record<string, unknown>; reason?: string }, opts) => api.runModuleAction(m.branch, m.id, i.key, spec.key, body, { signal: opts.signal }));
  const errorOf = (f: ModuleField) => {
    const v = values[f.key] ?? '';
    if (isBlank(v)) return isRequired(f, true, values, inputs) && f.default === null ? ('REQUIRED' as const) : null;
    return checkValue(f, v, data.environment === 'PRODUCTION');
  };
  const valid = inputs.every((f) => !errorOf(f)) && (!spec.mutating || reason.trim() !== '');
  const run = () =>
    void write.run({
      input: Object.fromEntries(inputs.map((f) => [f.key, payloadValue(f, values[f.key] ?? '')])),
      ...(spec.mutating ? { reason: reason.trim() } : {}),
    });
  return (
    <Panel
      title={text(spec.label)}
      description={
        <span className="flex flex-wrap items-center gap-2">
          <Badge tone={spec.mutating ? 'warn' : 'neutral'} icon={false}>
            {spec.mutating ? t('actions.changes') : t('actions.readOnly')}
          </Badge>
          <span>{t('actions.needs', { permission: permissionName(spec.permission) })}</span>
        </span>
      }
    >
      <div className="space-y-3">
        {inputs.length ? (
          <div className="grid items-start gap-3 sm:grid-cols-2">
            {inputs.map((f) => (
              <FieldInput key={f.key} field={f} value={values[f.key] ?? ''} onChange={(v) => setValues((x) => ({ ...x, [f.key]: v }))} required={isRequired(f, true, values, inputs)} error={errorOf(f)} />
            ))}
          </div>
        ) : null}
        {spec.mutating ? <ReasonField value={reason} onChange={setReason} /> : null}
        <Button size="sm" onClick={run} disabled={!valid || write.busy || !i.enabled} title={!i.enabled ? t('actions.needsOn') : undefined}>
          <Play /> {write.busy ? t('actions.running') : t('actions.run')}
        </Button>
        {!i.enabled ? <p className="text-xs text-muted-foreground">{t('actions.needsOn')}</p> : null}
        {write.state.phase === 'done' ? <ActionResultView result={write.state.result} /> : null}
        {write.state.phase === 'error' ? <ModuleError error={write.state.error} fields={[...fields, ...inputs]} /> : null}
      </div>
    </Panel>
  );
}

function ActionsPane({ data, fields }: PaneProps) {
  const { t } = useTranslation('modules');
  const actions = data.manifest.admin_actions;
  if (!actions.length) return <EmptyState title={t('actions.none')} />;
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('actions.hint')}</p>
      {actions.map((a) => (
        <ActionCard key={a.key} data={data} spec={a} fields={fields} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trang nghiệp vụ

function PagesPane({ data, branch }: PaneProps) {
  const { t } = useTranslation('modules');
  const text = useText();
  const pages = data.manifest.pages;
  // A hosting connection's servers, accounts and packages (HostBill's "Servers" and "Accounts on server").
  const hosting = branch === 'hosting' ? <HostingInstancePanel module={data.manifest.id} instanceKey={data.instance.key} canManage={data.can_manage} /> : null;
  if (!pages.length) return hosting ?? <EmptyState title={t('pages.none')} />;
  return (
    <div className="space-y-4">
      {hosting}
      <p className="text-sm text-muted-foreground">{t('pages.hint')}</p>
      {pages.map((p) => {
        if (p.target.kind === 'route') {
          return (
            <Panel
              key={p.key}
              title={text(p.label)}
              actions={
                <Button size="sm" variant="outline" asChild>
                  <Link to={p.target.value}>
                    <ExternalLink /> {t('pages.open')}
                  </Link>
                </Button>
              }
            >
              <code className="text-xs text-muted-foreground">{p.target.value}</code>
            </Panel>
          );
        }
        const Page = modulePage(p.target.value);
        return (
          <Panel key={p.key} title={text(p.label)}>
            {Page ? <Page /> : <p className="text-sm text-muted-foreground">{t('pages.missing')}</p>}
          </Panel>
        );
      })}
    </div>
  );
}

/** "Bật" / "Tắt" in the instance page's header. */
function InstanceToggle({ data, env, onChanged }: { data: ModuleInstanceDetail; env: IntegrationEnvironment; onChanged: () => void }) {
  const { t } = useTranslation('modules');
  const text = useText();
  const [open, setOpen] = useState(false);
  const i: ModuleInstance = data.instance;
  if (!data.can_manage || !i.active || i.pending) return null;
  const on = !i.enabled;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        {on ? t('card.switchOn') : t('card.switchOff')}
      </Button>
      <ChangeDialog open={open} onOpenChange={setOpen} title={on ? t('toggle.onTitle', { name: text(data.manifest.name) }) : t('toggle.offTitle', { name: text(data.manifest.name) })}>
        <ToggleForm manifest={data.manifest} instance={i} on={on} env={env} onClose={() => setOpen(false)} onDone={onChanged} />
      </ChangeDialog>
    </>
  );
}
